import { useEffect, useState } from "react";

import { api } from "../api/client";
import { normalizeNip } from "./useNipLookup";

/**
 * Status kontrahenta w wykazie podatników VAT („biała lista", MF).
 *
 * Osobny hak od `useNipLookup`, choć oba pytają o ten sam numer, bo pytają
 * o co innego: REGON mówi, **kto to jest**, wykaz — **czy z tej faktury jest
 * co odliczyć**. Zlepienie ich w jedno wywołanie związałoby ze sobą dwa
 * rejestry o różnej dostępności i różnych limitach, a wtedy awaria GUS-u
 * zabierałaby ze sobą odpowiedź Ministerstwa.
 *
 * Wynik jest informacją, nie blokadą: sprzedawca wyrejestrowany z VAT to
 * powód do wyjaśnienia przed odliczeniem, a nie do odmowy zapisania kosztu,
 * który przecież został poniesiony.
 */

export interface VatWhitelist {
  status:
    | "assigned"
    | "not_assigned"
    | "active"
    | "inactive"
    | "not_in_register"
    | "out_of_scope"
    | "unavailable";
  nip: string;
  checked_on: string | null;
  subject_name: string;
  status_vat: string;
  request_id: string;
  source: string;
  warnings: string[];
}

export interface VatWhitelistState {
  wynik: VatWhitelist | null;
  trwa: boolean;
}

export function useVatWhitelist(nip: string): VatWhitelistState {
  const numer = normalizeNip(nip);
  const [stan, setStan] = useState<VatWhitelistState>({ wynik: null, trwa: false });

  useEffect(() => {
    if (numer.length !== 10) {
      setStan({ wynik: null, trwa: false });
      return;
    }

    let aktualne = true;
    setStan((poprzedni) => ({ ...poprzedni, trwa: true }));

    api
      .get<VatWhitelist>(`/registry/vat/${numer}`)
      .then((wynik) => {
        if (aktualne) setStan({ wynik, trwa: false });
      })
      .catch(() => {
        // Suma kontrolna jest już zgłoszona pod polem przez `useNipLookup` —
        // powtórzenie tego samego komunikatu drugi raz niczego nie dodaje.
        if (aktualne) setStan({ wynik: null, trwa: false });
      });

    return () => {
      aktualne = false;
    };
  }, [numer]);

  return stan;
}

/** Jedno zdanie do pokazania obok pola z numerem. Pusty łańcuch = nic nie mów. */
export function komunikatWykazu(stan: VatWhitelistState): string {
  if (stan.trwa || !stan.wynik) return "";
  const wynik = stan.wynik;
  if (wynik.status === "not_in_register") {
    return "Tego numeru nie ma w wykazie podatników VAT.";
  }
  if (wynik.status === "inactive") {
    return `Status w wykazie VAT: ${wynik.status_vat}.`;
  }
  if (wynik.status === "unavailable") {
    // Milczenie byłoby gorsze niż komunikat: brak ostrzeżenia wygląda
    // dokładnie tak samo jak „sprawdzone i w porządku".
    return "Wykazu VAT nie udało się odpytać — status sprzedawcy nieznany.";
  }
  return "";
}
